import { useState } from 'react';

// Groups start expanded until the user collapses or reopens them; that choice is remembered in
// this browser across sessions.
const STORAGE_KEY = 'sidebar-groups-open';

function readGroupChoices(): Record<string, boolean> {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) return {};

    return Object.fromEntries(
      Object.entries(stored).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === 'boolean'
      )
    );
  } catch {
    return {};
  }
}

function writeGroupChoice(groupId: string, open: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readGroupChoices(), [groupId]: open }));
  } catch {
    // Storage unavailable (private mode, quota): the group still toggles, it just isn't remembered.
  }
}

export default function useSidebarGroupOpen(groupId: string): [boolean, (open: boolean) => void] {
  const [open, setOpen] = useState(() => readGroupChoices()[groupId] ?? true);

  const changeOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    writeGroupChoice(groupId, nextOpen);
  };

  return [open, changeOpen];
}
